import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  memo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Input, Button, Spinner } from "reactstrap";
import {
  DragDropContext,
  Draggable,
  DraggingStyle,
  Droppable,
  DropResult,
  NotDraggingStyle,
} from "@hello-pangea/dnd";
import { GridStack } from "gridstack";
import "gridstack/dist/gridstack.min.css";
import { random } from "../../../utils/actions";

import setting from "../../../assets/images/icons/console-6.svg";
import lightSetting from "../../../assets/images/icons/console-6.svg";

import widgetMindMap from "../../../assets/images/svg/mindmap.svg";

import { screenState } from "../../../store/screen/slice";
import { useAppSelector } from "../../../store/hooks";

export interface IframeComponentHandle {
  sendMessageToIframe: (message: any, action: string) => void;
}

interface IframeComponentProps {
  config: any;
  onUpdate: any;
  actionEnd: any;
  setMinSize: any;
  mindMapEdit: any;
  setMindMapEdit: any;
  userPermission: any;
  fitToImage: any;
  setFitToImage: any;
  cooperate: any;
}

const Index = forwardRef<IframeComponentHandle, IframeComponentProps>(
  (props, ref) => {
    const { username } = useAppSelector(state => screenState(state));
    const {
      config,
      onUpdate,
      actionEnd,
      setMinSize,
      mindMapEdit,
      setMindMapEdit,
      userPermission,
      fitToImage,
      setFitToImage,
      cooperate,
    } = props;
    const mindMapRef = useRef(null);
    const iframeLoadHandlerRef = useRef<(() => void) | null>(null);
    const [showIframe, setShowIframe] = useState(false);
    const [showOnLoadMsg, setShowOnLoadMsg] = useState(true);
    const [bgtrans, setBgtrans] = useState(
      config.theme === "theme1" ? "0" : "1",
    );
    const latestDataRef = useRef({
      tempData: config.data.tempData,
      rawData: config.data.rawData,
    });
    useEffect(() => {
      latestDataRef.current = {
        tempData: config.data.tempData,
        rawData: config.data.rawData,
      };
    }, [config.data.tempData, config.data.rawData]);

    //
    const [iframeKey, setIframeKey] = useState(
      () =>
        `teacher-${config.data.cooperate}-${config.data.action}-${random(12)}`,
    );
    const [isWaitingForData, setIsWaitingForData] = useState(false);
    const [pendingAction, setPendingAction] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      sendMessageToIframe: (message: any, action: string) => {
        const mindMap: any = mindMapRef.current;
        if (!mindMap) return;

        const mindMapWindow = mindMap.contentWindow;
        if (mindMapWindow) {
          if (message.type === "mindmap-get-data") {
            setIsWaitingForData(true);
            setPendingAction(action);
            setTimeout(() => {
              mindMapWindow.postMessage(message, "*");
            }, 100);
          }
        }
      },
    }));

    useEffect(() => {
      if (
        config.data.mindMapImage &&
        config.data.action === "edit" &&
        config.data.cooperate === 0
      ) {
        const data = config.data;
        data.action = "view";
        onUpdate({
          id: config.id,
          data: data,
        });
        actionEnd(true);
      }
    }, []);

    useEffect(() => {
      if (config.data.mindMapImage) {
        setMindMapEdit(false);
      }
    }, [config.data.mindMapImage]);

    const handleMindMapMessage = useCallback(
      (event: any) => {
        const mindMap: any = mindMapRef.current;
        if (!mindMap) return;

        if (event.source !== mindMap.contentWindow) return;
        if (event.data.identifier === config.data.id) {
          if (event.data.type === "mindmap-export") {
            const DATA = event.data.data;
            const MINDMAP_IMG = event.data.image;
            const data = config.data;

            data.rawData = DATA;
            data.tempData = DATA;
            data.mindMapImage = MINDMAP_IMG;
            data.action = "view";
            const update: any = { id: config.id, data: data };

            if (!username && !config?.data.studentName) {
              onUpdate(update);
              actionEnd(true);
              setFitToImage(true);
              setMindMapEdit(false);
            }
          }

          if (event.data.type === "mindmap-data") {
            const DATA = event.data.data;
            const data = config.data;

            data.tempData = DATA;
            latestDataRef.current = {
              tempData: DATA,
              rawData: data.rawData,
            };

            if (!username && !config?.data.studentName) {
              let newCooperate = data.cooperate;
              if (pendingAction === "switchToCooperate") {
                newCooperate = 1;
                data.cooperate = 1;
                data.action = "edit";
              } else if (pendingAction === "switchToNormal") {
                newCooperate = 0;
                data.cooperate = 0;
                data.action = "edit";
              }

              const update: any = { id: config.id, data: data };
              onUpdate(update);
              actionEnd(true);
              setTimeout(() => {
                setIframeKey(
                  `teacher-${newCooperate}-${data.action}-${bgtrans}-${random(
                    12,
                  )}`,
                );

                setIsWaitingForData(false);
                setPendingAction(null);
              }, 500);
            }
          }
        }
      },
      [
        config.id,
        config.data,
        username,
        pendingAction,
        onUpdate,
        actionEnd,
        setFitToImage,
        setMindMapEdit,
        setIsWaitingForData,
        setPendingAction,
      ],
    );

    useEffect(() => {
      if (username) return;

      const mindMap: any = mindMapRef.current;
      if (!mindMap) return;

      const mindMapWindow = mindMap.contentWindow;
      if (!mindMapWindow) return;

      const checkMindMap = () => {
        const mindMap: any = mindMapRef.current;
        if (!mindMap) {
          return;
        }

        const mindMapWindow = mindMap.contentWindow;
        if (!mindMapWindow) {
          return;
        }

        const handleIframeLoad = () => {
          const dataToSend =
            latestDataRef.current.tempData ||
            latestDataRef.current.rawData ||
            "";
          const currentWindow = mindMap.contentWindow;
          currentWindow.postMessage(
            {
              type: "mindmap-init",
              data: dataToSend,
            },
            "*",
          );
        };

        iframeLoadHandlerRef.current = handleIframeLoad;

        mindMap.addEventListener("load", handleIframeLoad);

        if (
          mindMap.contentDocument &&
          mindMap.contentDocument.readyState === "complete"
        ) {
          handleIframeLoad();
        }

        return () => {
          if (iframeLoadHandlerRef.current) {
            mindMap.removeEventListener("load", iframeLoadHandlerRef.current);
          }
        };
      };

      const timer = setTimeout(checkMindMap, 0);

      return () => {
        clearTimeout(timer);
      };
    }, [
      username,
      mindMapEdit,
      config.data.rawData,
      config.data.tempData,
      bgtrans,
      cooperate,
      isWaitingForData,
      pendingAction,
    ]);

    useEffect(() => {
      if (username) return;

      window.addEventListener("message", handleMindMapMessage);

      return () => {
        window.removeEventListener("message", handleMindMapMessage);
      };
    }, [username, handleMindMapMessage]);

    useEffect(() => {
      if (config.theme) {
        const mindMap: any = mindMapRef.current;
        if (!mindMap) return;
        setShowOnLoadMsg(true);
        if (config.theme === "theme1") {
          setBgtrans("0");
        } else if (config.theme === "theme2") {
          setBgtrans("1");
        }
      }
    }, [config.theme]);

    useEffect(() => {
      if (
        username &&
        config.data.cooperate === 1 &&
        config.data.action === "edit"
      ) {
        const timer = setTimeout(() => {
          setShowIframe(true);
        }, 2000);

        return () => clearTimeout(timer);
      } else {
        setShowIframe(false);
      }
    }, [username, config.data.cooperate, config.data.action]);

    const handleIframeOnload = () => {
      setShowOnLoadMsg(false);
    };

    return (
      <React.Fragment>
        <div style={{ height: "100%", width: "100%", position: "absolute" }}>
          {(!username && mindMapEdit) ||
          (!username &&
            mindMapEdit &&
            (config.data.rawData || config.data.tempData)) ||
          (!username &&
            !mindMapEdit &&
            (config.data.rawData || config.data.tempData)) ||
          (!username && !mindMapEdit && config.data.mindMapImage) ||
          (username &&
            (!config.data.rawData ||
              config.data.rawData ||
              config.data.tempData) &&
            config.data.cooperate === 1 &&
            config.data.action === "edit") ||
          (username &&
            config.data.mindMapImage &&
            config.data.action === "view") ? (
            <>
              {!username &&
              !mindMapEdit &&
              config.data.mindMapImage &&
              config.data.action === "view" ? (
                <div style={{ height: "100%", width: "100%" }}>
                  <img
                    src={config.data.mindMapImage}
                    style={{ height: "100%", width: "100%" }}
                    alt=""
                    onLoad={e => {
                      if (fitToImage) {
                        const img = e.currentTarget;
                        const aspectRatio =
                          img.naturalHeight / img.naturalWidth;
                        const calculatedHeight = 400 * aspectRatio;
                        onUpdate({
                          id: config.id,
                          baseHeight: calculatedHeight,
                          height: calculatedHeight,
                          width: 400,
                          minHeight: 50,
                          minWidth: 50,
                        });
                        setMinSize({
                          minWidth: 50,
                          minHeight: 50,
                        });
                        actionEnd(true);
                        setFitToImage(false);
                      }
                    }}
                  />
                </div>
              ) : null}

              {/*  */}
              {(!username && mindMapEdit) ||
              (!username &&
                mindMapEdit &&
                (config.data.rawData || config.data.tempData)) ||
              (!username &&
                !mindMapEdit &&
                (config.data.rawData || config.data.tempData) &&
                (config.data.cooperate === 0 || config.data.cooperate === 1) &&
                config.data.action === "edit") ? (
                <div
                  className={`bg-screen mindMap-edit ${
                    showOnLoadMsg ? "showOnLoadMsg" : ""
                  } ${bgtrans === "1" ? "hideMindMapBg" : ""}`}
                  style={{ height: "100%", width: "100%" }}
                >
                  <iframe
                    key={iframeKey}
                    data-key={iframeKey}
                    ref={mindMapRef}
                    src={`https://www.teachersay.com/mindmap/#/?action=init&role=teacher&userName=${`teacher_${config.id}`}&roomName=${`room_${config.id}`}&cooperate=${
                      config.data.cooperate ? 1 : 0
                    }&mode=edit${bgtrans === "1" ? "&bgtrans=1" : ""}`}
                    title="Mind Map"
                    style={{ border: "none", overflow: "hidden" }}
                    width="100%"
                    height="100%"
                    allowFullScreen
                    onLoad={handleIframeOnload}
                  ></iframe>
                </div>
              ) : null}
              {/*  */}
              {username &&
              config.data.mindMapImage &&
              config.data.action === "view" ? (
                <div style={{ height: "100%", width: "100%" }}>
                  <img
                    src={config.data.mindMapImage}
                    style={{ height: "100%", width: "100%" }}
                    alt=""
                  />
                </div>
              ) : null}
              {username &&
              config.data.cooperate === 1 &&
              config.data.action === "edit" ? (
                <div
                  className={`bg-screen mindMap-edit ${
                    showOnLoadMsg ? "showOnLoadMsg" : ""
                  } ${bgtrans === "1" ? "hideMindMapBg" : ""}`}
                  style={{ height: "100%", width: "100%" }}
                >
                  {showIframe ? (
                    <iframe
                      key={`${userPermission}-${config.data.cooperate}-${config.data.action}-${bgtrans}`}
                      ref={mindMapRef}
                      src={`https://www.teachersay.com/mindmap/#/?action=join&role=student&userName=${`${username}_${config.id}`}&roomName=${`room_${config.id}`}&cooperate=1&mode=edit${
                        bgtrans === "1" ? "&bgtrans=1" : ""
                      }`}
                      title="Mind Map"
                      style={{ border: "none", overflow: "hidden" }}
                      width="100%"
                      height="100%"
                      name={`Mind Map student ${config.id}`}
                      allowFullScreen
                      onLoad={handleIframeOnload}
                    ></iframe>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <>
              {!username ? (
                <div
                  className="relative flex h-full flex-col items-center justify-center p-6 bg-screen  overflow-hidden"
                  style={{ cursor: "auto" }}
                >
                  <div className="widget-head">
                    <img
                      className="widget-img-icon"
                      src={widgetMindMap}
                      alt=""
                    />
                  </div>
                  <p className="text-dark-500 mb-6 px-4 text-center text-xl font-semibold transition-[color]">
                    心智圖
                  </p>
                  <Button
                    color="light"
                    className={`ts-playBtn settingBtn btn-load rounded-pill text-sm px-4 h-12 font-semibold`}
                    onClick={(e: any) => {
                      setMindMapEdit(true);
                      const data = config.data;
                      data.action = "edit";
                      onUpdate({
                        id: config.id,
                        baseHeight: 600,
                        height: 600,
                        width: 720,
                        data: data,
                      });
                      actionEnd(true);
                    }}
                    data-interaction-target-type="false"
                    disabled={!!username}
                  >
                    <div className="relative justify-center h-100 w-100">
                      <span className="flex items-center justify-center w-100">
                        設定
                      </span>
                    </div>
                  </Button>
                </div>
              ) : (
                <div
                  className="relative flex h-full flex-col items-center justify-center p-6 bg-screen  overflow-hidden"
                  style={{ cursor: "auto" }}
                >
                  <div className="mindMap-teacher-editing">
                    <h1>老師編輯中...</h1>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {/* {username && !userPermission? (
        <div className="absolute h-100 w-100 top-0 start-0" style={{ zIndex: 2 }}></div>
      ) : null} */}
      </React.Fragment>
    );
  },
);

Index.displayName = "Index";

export default memo(Index);
